/**
 * Instance rows — the pure rules behind the CCA instance table.
 *
 * Why a CCA module: a CCA row carries Quantity and Total Number of Hours per
 * Month (as strings), an `instance_name`, and the server's `adjusted` flag —
 * none of which the EIA row has — and legacy maps, edits and re-sends it
 * differently. The exports mirror components/eia/instanceRows.js name for name
 * (toTableRow, toUploadedRow, mergeRevalidated, requiredRowFields,
 * missingRowFields, markRequired, applyEdit, EDITABLE_FIELDS), so the page can
 * pick the module by product. Helpers that are the same for both products
 * (remarks, problem-first sort, unsaved check, cell format, row ids) are
 * re-exported from the EIA module, not copied.
 *
 * Ported from legacy ManageAccount.vue (addInstanceClick CCA mapping :702-719),
 * UploadDownloadTemplate.vue (onFileChanged CCA mapping :609-628) and
 * InstancesListTable.vue (ccaHeadersBase :339-418, OCI columns :595-627,
 * requiredFieldsFor :1041-1054, handleQuantityChange / handlehourschange
 * :1205-1221, saveEdit CCA body and mapping :1243-1313).
 */
import { applyEdit as applyEditShared, newRowId } from '@components/eia/instanceRows';

import { CCA_FIELD, requiredCcaFields } from './ccaFormRules';

export {
  countRemarks,
  formatCell,
  hasErrorRemark,
  hasRemarks,
  hasUnsavedRows,
  isWarning,
  newRowId,
  remarkFor,
  sortProblemsFirst,
  toApiRow,
  toSavedRow,
  withoutErrorRows,
} from '@components/eia/instanceRows';

const QUANTITY = CCA_FIELD.QUANTITY;
const HOURS = CCA_FIELD.HOURS;

function blank(value) {
  return value === null || value === undefined || value === '';
}

/**
 * Quantity / Hours as the table keeps them: strings. Legacy called
 * `.toString()` straight on the server's value and crashed on a null; a
 * missing value is '' here, which the Required check then flags.
 */
function toText(value) {
  return value === null || value === undefined ? '' : String(value);
}

/** `Remarks` as a list, whatever the server sent (legacy falls back to {}). */
function remarksOf(row) {
  return Array.isArray(row?.Remarks) ? row.Remarks : [];
}

/** OCI OCPU / Memory as strings when present (legacy `?.toString()`). */
function ociAsText(row) {
  const out = {};
  for (const field of [CCA_FIELD.OCPU, CCA_FIELD.MEMORY]) {
    if (row[field] !== null && row[field] !== undefined) out[field] = String(row[field]);
  }
  return out;
}

/**
 * The table row for an instance the server has validated (Add Instance): what
 * was sent, with the server's version laid over it — legacy's CCA mapping
 * (:702-719): quantity / hours as strings, the server's pricing model, uuid,
 * instance_name and os_type, its `adjusted` flag (false when absent), never
 * saved yet.
 */
export function toTableRow(sent, validated) {
  const row = {
    ...sent,
    ...validated,
    [QUANTITY]: toText(validated?.[QUANTITY] ?? sent[QUANTITY]),
    [HOURS]: toText(validated?.[HOURS] ?? sent[HOURS]),
    [CCA_FIELD.PRICING_MODEL]: validated?.pricingModel ?? sent[CCA_FIELD.PRICING_MODEL],
    [CCA_FIELD.UUID]: toText(validated?.uuid ?? sent[CCA_FIELD.UUID]),
    instance_name: validated?.instance_name ?? sent.instance_name,
    Remarks: remarksOf(validated),
    isSaved: false,
    adjusted: Boolean(validated?.adjusted),
    _rowId: newRowId(),
  };
  return { ...row, ...ociAsText(row) };
}

/**
 * A row from an uploaded file (file-upload/validate `Data`) as a table row —
 * the fields legacy keeps for CCA (:609-628), OCPU / Memory only for OCI.
 *
 * Fixed: `instance_name` is kept (legacy dropped it here, though an edit
 * re-sends it); `adjusted` is the server's value when it sent one (legacy's
 * `adjusted || true` made it always true).
 */
export function toUploadedRow(instance, provider) {
  const oci = String(provider ?? '').toUpperCase() === 'OCI';
  return {
    uuid: toText(instance?.uuid),
    instance_name: instance?.instance_name,
    cloud_csp: instance?.cloud_csp,
    region: instance?.region,
    [CCA_FIELD.INSTANCE_TYPE]: instance?.[CCA_FIELD.INSTANCE_TYPE],
    [QUANTITY]: toText(instance?.[QUANTITY]),
    [HOURS]: toText(instance?.[HOURS]),
    [CCA_FIELD.PRICING_MODEL]: instance?.pricingModel,
    [CCA_FIELD.OS]: instance?.os_type,
    Remarks: remarksOf(instance),
    isSaved: false,
    adjusted: instance?.adjusted ?? true,
    ...(oci
      ? {
          [CCA_FIELD.OCPU]: instance?.ocpu?.toString(),
          [CCA_FIELD.MEMORY]: instance?.memory?.toString(),
        }
      : {}),
    _rowId: newRowId(),
  };
}

/**
 * The row legacy sends to /input/validate when a CCA cell is edited
 * (saveEdit :1243-1259): only these keys — no Remarks / isSaved / adjusted —
 * quantity / hours / uuid as strings, os_type lower-cased, OCPU / Memory on
 * OCI.
 */
export function toValidateRow(row, provider) {
  const oci = String(provider ?? '').toUpperCase() === 'OCI';
  return {
    cloud_csp: row.cloud_csp,
    region: row.region,
    [CCA_FIELD.INSTANCE_TYPE]: row[CCA_FIELD.INSTANCE_TYPE],
    [QUANTITY]: toText(row[QUANTITY]),
    [HOURS]: toText(row[HOURS]),
    [CCA_FIELD.PRICING_MODEL]: row[CCA_FIELD.PRICING_MODEL],
    uuid: toText(row.uuid),
    instance_name: row.instance_name,
    os_type: row.os_type == null ? row.os_type : String(row.os_type).toLowerCase(),
    ...(oci
      ? {
          [CCA_FIELD.OCPU]: row[CCA_FIELD.OCPU]?.toString(),
          [CCA_FIELD.MEMORY]: row[CCA_FIELD.MEMORY]?.toString(),
        }
      : {}),
  };
}

/**
 * An edited row after the server checked it: the server's version laid over
 * the edit, same table id (legacy saveEdit CCA mapping :1293-1313 — adjusted
 * true, never saved).
 *
 * Fixed: legacy's failure path rebuilt the row from a partial copy and lost
 * `uuid` and `instance_name`; here a failed check simply leaves the row as it
 * was (the page keeps the original), and a successful one falls back to the
 * edited row for anything the server leaves out.
 */
export function mergeRevalidated(edited, validated) {
  const row = {
    ...edited,
    ...validated,
    [QUANTITY]: toText(validated?.[QUANTITY] ?? edited[QUANTITY]),
    [HOURS]: toText(validated?.[HOURS] ?? edited[HOURS]),
    uuid: toText(validated?.uuid ?? edited.uuid),
    instance_name: validated?.instance_name ?? edited.instance_name,
    Remarks: remarksOf(validated),
    isSaved: false,
    adjusted: true,
    _rowId: edited._rowId,
  };
  return { ...row, ...ociAsText(row) };
}

/* --- the table (legacy InstancesListTable.vue, CCA) ----------------------- */

/**
 * The CCA columns, legacy's titles and order (ccaHeadersBase :339-418); on
 * OCI, OCPU and Memory follow Pricing Model (:601-626). The size column is
 * "Size" on every cloud, as legacy's table titles it.
 */
const CCA_COLUMNS = Object.freeze([
  Object.freeze({ key: CCA_FIELD.UUID, title: 'UUID / Instance Name', editable: false }),
  Object.freeze({ key: 'cloud_csp', title: 'Cloud', editable: false }),
  Object.freeze({ key: CCA_FIELD.REGION, title: 'Region', editable: true }),
  Object.freeze({ key: CCA_FIELD.INSTANCE_TYPE, title: 'Size', editable: true }),
  Object.freeze({ key: CCA_FIELD.OS, title: 'OS Type', editable: true }),
  Object.freeze({ key: QUANTITY, title: 'Quantity', editable: true }),
  Object.freeze({ key: HOURS, title: 'Total Number of Hours per Month', editable: true }),
  Object.freeze({ key: CCA_FIELD.PRICING_MODEL, title: 'Pricing Model', editable: true }),
]);

const CCA_OCI_COLUMNS = Object.freeze([
  Object.freeze({ key: CCA_FIELD.OCPU, title: 'OCPU', editable: true }),
  Object.freeze({ key: CCA_FIELD.MEMORY, title: 'Memory', editable: true }),
]);

/** @returns {{ key: string, title: string, editable: boolean }[]} */
export function ccaColumnsFor(provider) {
  return String(provider ?? '').toUpperCase() === 'OCI'
    ? [...CCA_COLUMNS, ...CCA_OCI_COLUMNS]
    : CCA_COLUMNS;
}

/**
 * The edit kind per field: a dropdown or a number box. UUID / Instance Name
 * and Cloud are not editable (legacy `editable: false`).
 */
export const EDITABLE_FIELDS = Object.freeze({
  [CCA_FIELD.REGION]: 'select',
  [CCA_FIELD.INSTANCE_TYPE]: 'select',
  [CCA_FIELD.OS]: 'select',
  [QUANTITY]: 'number',
  [HOURS]: 'number',
  [CCA_FIELD.PRICING_MODEL]: 'select',
  [CCA_FIELD.OCPU]: 'number',
  [CCA_FIELD.MEMORY]: 'number',
});

/**
 * Cells that go back to their old value when emptied, instead of being kept
 * as "Required" (legacy handleQuantityChange / handlehourschange :1205-1221).
 */
export const REVERT_WHEN_EMPTIED = Object.freeze([QUANTITY, HOURS]);

/**
 * The row after one cell changes. Emptying Quantity or Hours is no change at
 * all — the SAME row object comes back, so the page can tell and close the
 * editor without asking the server (legacy reverts and cancels the edit).
 * Everything else follows the shared rule (OCI: a switch to or from a
 * non-flex type clears OCPU / Memory).
 */
export function applyEdit(row, field, value, provider) {
  if (REVERT_WHEN_EMPTIED.includes(field) && blank(value)) return row;
  return applyEditShared(row, field, value, provider);
}

/**
 * Fields a table row must have before it is sent to the server — the same
 * list the Add Instance form requires (requiredCcaFields: region, size,
 * quantity, hours, pricing model, plus OCPU / Memory on an OCI flex type).
 *
 * Legacy checked region, size, quantity and hours here (requiredFieldsFor
 * :1041-1054) and let the server refuse a blank pricing model; pricing model
 * is added to match the EIA table, where the user asked that no request go
 * out until every mandatory cell is filled.
 */
export function requiredRowFields(row, provider) {
  return requiredCcaFields(provider, row[CCA_FIELD.INSTANCE_TYPE]);
}

/** Required fields this row leaves empty. */
export function missingRowFields(row, provider) {
  return requiredRowFields(row, provider).filter((field) => blank(row[field]));
}

/**
 * Keep an edit WITHOUT asking the server, flagging the empty required cells
 * "Required" (legacy applyRowLocally :1078-1104). Same rule as the EIA table:
 * only the required cells' remarks change; the server's other remarks stay.
 */
export function markRequired(row, provider) {
  const checked = requiredRowFields(row, provider);
  const missing = missingRowFields(row, provider);
  const stale = (r) =>
    missing.includes(r?.Field) || (checked.includes(r?.Field) && r?.Message === 'Required');
  return {
    ...row,
    Remarks: [
      ...remarksOf(row).filter((r) => !stale(r)),
      ...missing.map((field) => ({ Field: field, Message: 'Required' })),
    ],
    isSaved: false,
  };
}
